/* eslint-disable react/no-unstable-nested-components */
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import useDragScroll from 'hooks/useDragScroll'
import Helmet from 'components/utilities/Helmet'
import Table from 'components/organisms/TableV2'
import DebinIcon from 'assets/svg/DebinIcon'
import DownloadSVG from 'assets/svg/DownloadSVG'
import InfoSVG from 'assets/svg/infoSVG'
import { InfoCard, DebinCard, Tooltip } from 'components'
import { useDispatch } from 'react-redux'
import { showDrawer } from 'store/drawer/actions'
import { Drawers } from 'store/drawer/types'
import {
  ViewContainer,
  TableWrapper,
  ActivityContainer,
  ActivityText,
  CustomText,
  DebinCardsSection,
  DebinCardsSectionTitle,
  TitleWithTooltip,
  CardsCarousel
} from './styles'
import {
  cancelRecurrence,
  confirmRecurrence,
  getDebinMovements,
  getRecurrences
} from 'store/debin/thunks'
import { useAppSelector } from 'hooks/redux'
import { formatDate } from 'utils/date'
import { RecurrenceStatus } from 'store/debin/types'
import TwoFactorAuthentication from 'components/molecules/TwoFactorAuthentication'
import YourAccountSkeleton from 'assets/skeletons/YourAccountSkeleton'
import { PendingRecurrenceAction } from './types'
import { formatDateWithMonthOptional } from '@qirapagos/lib/utils/common'
import Status from 'components/atoms/Status'
import { getDebinStatus } from './utils'
import { useTable } from 'hooks/useTable'

const Debin: React.FC = () => {
  const dispatch = useDispatch()
  const { ref: carouselRef, onMouseDown } = useDragScroll()
  const [show2FA, setShow2FA] = useState(false)
  const { datesToFilter, handleDatesToFilter } = useTable()

  const [pendingAction, setPendingAction] =
    useState<PendingRecurrenceAction | null>(null)

  useEffect(() => {
    if (!show2FA) return

    const handlePopState = (event: PopStateEvent) => {
      event.preventDefault()

      setShow2FA(false)
      setPendingAction(null)
    }

    window.addEventListener('popstate', handlePopState)

    return () => {
      window.removeEventListener('popstate', handlePopState)
    }
  }, [show2FA])

  const onPressAccept = (recurrenceId: number) => {
    window.history.pushState({ modal: '2fa' }, '', window.location.pathname)
    setPendingAction({ type: 'ACCEPT', recurrenceId })
    setShow2FA(true)
  }

  const onPressReject = (recurrenceId: number) => {
    window.history.pushState({ modal: '2fa' }, '', window.location.pathname)
    setPendingAction({ type: 'REJECT', recurrenceId })
    setShow2FA(true)
  }

  useEffect(() => {
    dispatch(getRecurrences())
    dispatch(getDebinMovements())
    return () => {
      handleDatesToFilter(null, null)
    }
  }, [dispatch])

  const { isLoading, recurrences, debinMovements, isDebinMovementsLoading } =
    useAppSelector(state => state.debin)

  const debinCards = useMemo(
    () =>
      recurrences.map(recurrence => ({
        id: recurrence.id,
        externalId: recurrence.externalId,
        company: recurrence.benefit,
        sellerTaxId: recurrence.sellerTaxId,
        date: formatDate(recurrence.createdAt),
        amount: (
          Number(recurrence.amountPerQuote) * recurrence.quotes
        ).toLocaleString('es-AR', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2
        }),
        installments: recurrence.quotes,
        concept: recurrence.concept,
        status: recurrence.status,
        authorizationStatus: recurrence.authorizationStatus,
        cancellationStatus: recurrence.cancellationStatus,
        validForRefund: recurrence.validForRefund,
        paidQuotes: recurrence.paidQuotes,
        hasQuoteInProgress: recurrence.hasQuoteInProgress
      })),
    [recurrences]
  ).filter(r => r.status !== RecurrenceStatus.DEFINITELY_INACTIVE)

  const tableData = useMemo(() => {
    if (!datesToFilter.dateFrom || !datesToFilter.dateTo) return debinMovements

    const from = new Date(datesToFilter.dateFrom)
    const to = new Date(datesToFilter.dateTo)
    from.setHours(0, 0, 0, 0)
    to.setHours(23, 59, 59, 999)

    return debinMovements.filter(movement => {
      const movementDate = new Date(movement.date)
      return movementDate >= from && movementDate <= to
    })
  }, [debinMovements, datesToFilter])

  const handleDownloadClick = useCallback(() => {
    dispatch(showDrawer(Drawers.DOWNLOAD_DEBIN, { debinData: debinMovements }))
  }, [dispatch, debinMovements])

  if (isLoading || isDebinMovementsLoading) {
    return <YourAccountSkeleton />
  }

  if (show2FA && pendingAction) {
    return (
      <ViewContainer>
        <TwoFactorAuthentication
          onlyByEmail={false}
          isDebinAuth
          callback={async () => {
            setShow2FA(false)
            switch (pendingAction.type) {
              case 'ACCEPT':
                await dispatch(
                  confirmRecurrence(pendingAction.recurrenceId, true)
                )
                break
              case 'REJECT':
                await dispatch(
                  confirmRecurrence(pendingAction.recurrenceId, false)
                )
                break
              case 'CANCEL':
                await dispatch(cancelRecurrence(pendingAction.recurrenceId))
                break
              default:
                break
            }
            setPendingAction(null)
            await dispatch(getRecurrences())
          }}
        />
      </ViewContainer>
    )
  }

  return (
    <ViewContainer>
      <Helmet title="Pagos con DEBIN" />
      <DebinCardsSection>
        <TitleWithTooltip>
          <DebinCardsSectionTitle>
            Pagos con DEBIN activos
          </DebinCardsSectionTitle>
          <Tooltip
            label="El dinero se debitará automáticamente una vez que confirmes la operación"
            hasArrow={false}
            placement="right"
          >
            <InfoSVG />
          </Tooltip>
        </TitleWithTooltip>
        <CardsCarousel ref={carouselRef} onMouseDown={onMouseDown}>
          {debinCards.length > 0
            ? (
                debinCards.map(card => (
                  <DebinCard
                    key={card.id}
                    company={card.company}
                    sellerTaxId={card.sellerTaxId}
                    date={card.date}
                    amount={card.amount}
                    installments={card.installments}
                    concept={card.concept}
                    status={card.status}
                    authorizationStatus={card.authorizationStatus}
                    onReject={() => onPressReject(card.id)}
                    onConfirm={() => onPressAccept(card.id)}
                    onView={() => {
                      dispatch(showDrawer(Drawers.DEBIN_DETAIL, {
                        debinData: {
                          id: card.id,
                          externalId: card.externalId,
                          company: card.company,
                          sellerTaxId: card.sellerTaxId,
                          date: card.date,
                          amount: card.amount,
                          installments: card.installments,
                          concept: card.concept,
                          status: card.status,
                          authorizationStatus: card.authorizationStatus,
                          cancellationStatus: card.cancellationStatus,
                          validForRefund: card.validForRefund,
                          paidQuotes: card.paidQuotes,
                          hasQuoteInProgress: card.hasQuoteInProgress
                        },
                        onRequestCancel: (id: number) => {
                          window.history.pushState(
                            { modal: '2fa' },
                            '',
                            window.location.pathname
                          )
                          setPendingAction({
                            type: 'CANCEL',
                            recurrenceId: id
                          })
                          setShow2FA(true)
                        }
                      })
                      )
                    }}
                  />
                ))
              )
            : (
              <InfoCard />
              )}
        </CardsCarousel>
      </DebinCardsSection>
      <TableWrapper>
        <Table
          data={tableData}
          title="Pagos con DEBIN"
          icon={<DownloadSVG />}
          onClick={handleDownloadClick}
          emptyText="Aún no tenés pagos DEBIN registrados"
          hasDateFilter
          onFilter={handleDatesToFilter}
          config={[
            {
              id: 'activityData',
              header: 'Actividad',
              meta: {
                formatter: (value: string) => (
                  <ActivityContainer>
                    <DebinIcon />
                    <ActivityText>{value}</ActivityText>
                  </ActivityContainer>
                )
              }
            },
            {
              id: 'internalStatus',
              header: 'Estado',
              meta: {
                // eslint-disable-next-line react/no-unstable-nested-components
                formatter: (value: number) => {
                  return (
                    <ActivityContainer>
                      <Status statusType={getDebinStatus(value)} />
                    </ActivityContainer>
                  )
                }
              }
            },
            {
              id: 'date',
              header: 'Fecha',
              meta: {
                formatter: (value: Date) => {
                  return (
                    <CustomText>
                      {formatDateWithMonthOptional(value.toString())}
                    </CustomText>
                  )
                }
              }
            },
            {
              id: 'amount',
              header: 'Monto',
              meta: {
                formatter: (value: number) => (
                  <CustomText>
                    $
                    {value.toLocaleString('es-AR')}
                  </CustomText>
                )
              }
            },
            {
              id: 'concept',
              header: 'Concepto',
              meta: {
                formatter: (value: string) => <CustomText>{value}</CustomText>
              }
            }
            // TO DO: if we keep this, we have make a drawer with details.
            // {
            //   id: TABLE.ACTION,
            //   header: '',
            //   meta: {
            //     actions: ({ row }: { row: { original: DebinPayment } }) => [
            //       {
            //         title: 'Ver detalle',
            //         onPress: () => {
            //           console.log('Ver detalle:', row.original)
            //         }
            //       }
            //     ]
            //   }
            // }
          ]}
          isLoading={false}
        />
      </TableWrapper>
    </ViewContainer>
  )
}

export default Debin
